import React, { useState } from 'react';
import { useMonitoring } from '../context/MonitoringContext';

export default function Logs() {
    const {
        updateLogs,
        priceLogs,
        loadingLogs,
        loadingServers,
        refreshData,
        groups,
    } = useMonitoring();

    const [searchFilter, setSearchFilter] = useState('');
    const [groupFilter, setGroupFilter] = useState('all');
    const [triggerTypeFilter, setTriggerTypeFilter] = useState('all');

    const filteredUpdates = updateLogs.filter((log) => {
        const matchesSearch =
            !searchFilter ||
            (log.serverId && log.serverId.toLowerCase().includes(searchFilter.toLowerCase())) ||
            (log.groupName && log.groupName.toLowerCase().includes(searchFilter.toLowerCase())) ||
            (log.status && log.status.toLowerCase().includes(searchFilter.toLowerCase()));
        const matchesGroup = groupFilter === 'all' || log.groupName === groupFilter;
        return matchesSearch && matchesGroup;
    });

    const filteredPrices = priceLogs.filter((log) => {
        const matchesSearch =
            !searchFilter ||
            (log.serverId && log.serverId.toLowerCase().includes(searchFilter.toLowerCase())) ||
            (log.groupName && log.groupName.toLowerCase().includes(searchFilter.toLowerCase())) ||
            (log.status && log.status.toLowerCase().includes(searchFilter.toLowerCase()));
        const matchesGroup = groupFilter === 'all' || log.groupName === groupFilter;
        const matchesTrigger =
            triggerTypeFilter === 'all' ||
            (log.triggerType && log.triggerType.toLowerCase().includes(triggerTypeFilter.toLowerCase()));
        return matchesSearch && matchesGroup && matchesTrigger;
    });

    return (
        <>
            <header className="dashboard-header">
                <div className="dashboard-title-area">
                    <h1>Activity Logs & Reports</h1>
                    <p>Audit trail of database maintenance, software deployments, and price triggers.</p>
                </div>
            </header>

            <div className="reports-layout">
                <section className="portal-panel">
                    <div className="reports-header-row">
                        <h2 className="portal-panel-title">Activity Logs & Reports</h2>
                        <div className="reports-filters">
                            <input
                                type="text"
                                className="filter-input"
                                placeholder="Search logs..."
                                value={searchFilter}
                                onChange={(e) => setSearchFilter(e.target.value)}
                            />
                            <select
                                className="filter-select"
                                value={groupFilter}
                                onChange={(e) => setGroupFilter(e.target.value)}
                            >
                                <option value="all">All Groups</option>
                                {groups.map((g) => (
                                    <option key={g} value={g}>{g}</option>
                                ))}
                            </select>
                            <select
                                className="filter-select"
                                value={triggerTypeFilter}
                                onChange={(e) => setTriggerTypeFilter(e.target.value)}
                            >
                                <option value="all">All Price Triggers</option>
                                <option value="daily">Daily Updates</option>
                                <option value="full">Full Updates</option>
                            </select>
                            <button className="refresh-btn" onClick={refreshData} disabled={loadingLogs || loadingServers}>
                                {loadingLogs || loadingServers ? 'Refreshing...' : 'Refresh'}
                            </button>
                        </div>
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '24px' }}>
                        <div>
                            <h3 className="modal-section-title">Software Release Update Logs</h3>
                            <div className="logs-table-container" style={{ maxHeight: '300px' }}>
                                {filteredUpdates.length === 0 ? (
                                    <div style={{ padding: '16px', color: 'var(--text-muted)', fontSize: 'var(--fs-xs)' }}>
                                        No software update logs match current filters.
                                    </div>
                                ) : (
                                    <table className="logs-table">
                                        <thead>
                                            <tr>
                                                <th>Timestamp</th>
                                                <th>Server Instance</th>
                                                <th>Group</th>
                                                <th>Software Component</th>
                                                <th>Target Version</th>
                                                <th>Status</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {filteredUpdates.map((log) => (
                                                <tr key={log.id}>
                                                    <td style={{ whiteSpace: 'nowrap' }}>{new Date(log.timestamp).toLocaleString()}</td>
                                                    <td style={{ fontFamily: 'monospace' }}>{log.serverId}</td>
                                                    <td>{log.groupName}</td>
                                                    <td>{log.software}</td>
                                                    <td>{log.version}</td>
                                                    <td>
                                                        <span
                                                            className="portal-badge"
                                                            style={{
                                                                backgroundColor: log.status.toLowerCase().includes('success') || log.status.toLowerCase().includes('done') ? '#193c31' : '#382914',
                                                                color: log.status.toLowerCase().includes('success') || log.status.toLowerCase().includes('done') ? '#52c48d' : '#EF9F27',
                                                            }}
                                                        >
                                                            {log.status}
                                                        </span>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                )}
                            </div>
                        </div>

                        <div>
                            <h3 className="modal-section-title">Price Update Activity Logs</h3>
                            <div className="logs-table-container" style={{ maxHeight: '300px' }}>
                                {filteredPrices.length === 0 ? (
                                    <div style={{ padding: '16px', color: 'var(--text-muted)', fontSize: 'var(--fs-xs)' }}>
                                        No price update logs match current filters.
                                    </div>
                                ) : (
                                    <table className="logs-table">
                                        <thead>
                                            <tr>
                                                <th>Timestamp</th>
                                                <th>Server Instance</th>
                                                <th>Group</th>
                                                <th>Trigger Type</th>
                                                <th>Status</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {filteredPrices.map((log) => (
                                                <tr key={log.id}>
                                                    <td style={{ whiteSpace: 'nowrap' }}>{new Date(log.timestamp).toLocaleString()}</td>
                                                    <td style={{ fontFamily: 'monospace' }}>{log.serverId}</td>
                                                    <td>{log.groupName}</td>
                                                    <td>{log.triggerType}</td>
                                                    <td>
                                                        <span
                                                            className="portal-badge"
                                                            style={{
                                                                backgroundColor: '#193c31',
                                                                color: '#52c48d',
                                                            }}
                                                        >
                                                            {log.status}
                                                        </span>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                )}
                            </div>
                        </div>
                    </div>
                </section>
            </div>
        </>
    );
}